import { humanizeToolName } from "@/lib/tool-display";

function stripPrefixes(message: string): string {
  return message
    .replace(/^error[:\s-]*/i, "")
    .replace(/^(?:\[[^\]]+\]\s*)+/, "")
    .trim();
}

export function humanizeErrorMessage(rawMessage: string): string {
  const message = stripPrefixes(rawMessage);
  if (!message) return "Something went wrong. Please try again.";

  if (/daemon (?:stream )?request failed/i.test(message)) {
    return "FreshVibe couldn’t reach the preview service. Please try again.";
  }

  if (/no active agent connection/i.test(message)) {
    return "FreshVibe lost its connection. Please reconnect and try again.";
  }

  return message;
}

// ─── Debug log summarisation ─────────────────────────────────────────────
// Converts raw internal debug messages into plain-language one-liners for
// the debug panel list view.  The raw message is still available in the
// detail modal when a user clicks through.

const SESSION_UPDATE_LABELS: Record<string, string> = {
  agent_thought_chunk: "Agent is thinking",
  agent_message_chunk: "Agent is responding",
  agent_message_end: "Agent finished responding",
  tool_call: "Running a tool",
  tool_call_update: "Tool in progress",
  available_commands_update: "Commands updated",
  end_turn: "Turn complete",
};

export function humanizeDebugMessage(message: string): string {
  // session_update: <type>
  const sessionMatch = message.match(/^session_update:\s*(\S+)/);
  if (sessionMatch) {
    return SESSION_UPDATE_LABELS[sessionMatch[1]] ?? `Session update: ${sessionMatch[1]}`;
  }

  // permission_request: <toolName>
  const permReqMatch = message.match(/^permission_request:\s*(.+)/);
  if (permReqMatch) {
    return `Asking for permission — ${humanizeToolName(permReqMatch[1])}`;
  }

  // permission_auto_response: <tool> -> <option>
  if (/^permission_auto_response:/i.test(message)) {
    return "Auto-approved";
  }

  // permission_preset_saved: <tool>
  if (/^permission_preset_saved:/i.test(message)) {
    return "Saved approval preference";
  }

  // permission_response: <optionId | cancelled>
  const permRespMatch = message.match(/^permission_response:\s*(.+)/);
  if (permRespMatch) {
    return permRespMatch[1] === "cancelled"
      ? "Permission cancelled"
      : "Permission granted";
  }

  // sendPrompt failed: <reason>
  if (/^sendPrompt failed:/i.test(message)) {
    return "Message failed to send";
  }

  // Fall through to humanizeErrorMessage for known error patterns, but
  // only when the entry looks like an error string (avoid mangling
  // legitimate non-error messages).
  if (/^error\b/i.test(message) || /\bdaemon\b/i.test(message)) {
    const humanized = humanizeErrorMessage(message);
    if (humanized !== stripPrefixes(message)) return humanized;
  }

  return message;
}
